import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { toast } from 'sonner';
import {
  PaginationControls,
  SearchBar,
  FilterTabs,
  usePagination,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../components/ListControls';
import { useWalletStore } from '../stores/useWalletStore';
import {
  CLIENT_FACILITY_REQUIREMENTS,
  CLIENT_FACILITIES,
  CLIENT_WALLET_ONCHAIN_IDS,
} from '../data/mockAdapter';

// ─── Types ──────────────────────────────────────────────────────────────────────

type EligibilityStatus = 'eligible' | 'partial' | 'not_eligible' | 'no_requirements';
type EligibilityFilter = 'all' | EligibilityStatus;

interface ComplianceRequirement {
  type: 'external_kyc_claim' | 'internal_claim';
  claimType: string;
  description: string;
}

interface Claim {
  type: string;
  issuer: string;
}

interface OnchainId {
  id: string;
  address: string;
  claims: Claim[];
  source: string;
}

interface Facility {
  id: string;
  name: string;
  settlementTokens: string[];
  collateralTokens: string[];
  interestRate: string;
  maxLTV: string;
  utilization: string;
  region: string;
  status: 'active' | 'paused';
  requirements: ComplianceRequirement[];
}

interface FacilityEligibility {
  facility: Facility;
  eligibility: EligibilityStatus;
  metClaims: string[];
  missingClaims: ComplianceRequirement[];
}

// Per spec there is exactly one Ascend-scoped OnchainID per user — filter the
// mock data to the Ascend-source entry only (or the first entry as fallback).
const ALL_MOCK_ONCHAIN_IDS: OnchainId[] = CLIENT_WALLET_ONCHAIN_IDS.default.map((entry) => ({
  id: entry.id,
  address: entry.address,
  source: entry.source,
  claims: entry.claims.map((claim) => ({ type: claim.type, issuer: claim.issuer })),
}));
const ASCEND_ONCHAIN_ID = ALL_MOCK_ONCHAIN_IDS.find((oid) => oid.source === 'ascend')
  ?? ALL_MOCK_ONCHAIN_IDS[0];
const MOCK_ONCHAIN_IDS: OnchainId[] = ASCEND_ONCHAIN_ID ? [ASCEND_ONCHAIN_ID] : [];

const FACILITIES: Facility[] = CLIENT_FACILITIES.map((facility) => ({
  id: facility.id,
  name: facility.name,
  settlementTokens: facility.settlementTokens,
  collateralTokens: facility.collateralTokens,
  interestRate: facility.interestRate,
  maxLTV: facility.maxLTV,
  utilization: facility.utilization,
  region: facility.region,
  status: facility.status,
  requirements: CLIENT_FACILITY_REQUIREMENTS[facility.id] ?? [],
}));

// ─── Compliance Check Logic ─────────────────────────────────────────────────────

function checkEligibility(facility: Facility, onchainIds: OnchainId[]): FacilityEligibility {
  if (facility.requirements.length === 0) {
    return { facility, eligibility: 'no_requirements', metClaims: [], missingClaims: [] };
  }

  const met: string[] = [];
  const missing: ComplianceRequirement[] = [];

  for (const req of facility.requirements) {
    const found = onchainIds.some((oid) => oid.claims.some((c) => c.type === req.claimType));
    if (found) met.push(req.claimType);
    else missing.push(req);
  }

  const eligibility: EligibilityStatus =
    missing.length === 0 ? 'eligible' : met.length > 0 ? 'partial' : 'not_eligible';

  return { facility, eligibility, metClaims: met, missingClaims: missing };
}

// ─── Config ─────────────────────────────────────────────────────────────────────

const ELIGIBILITY_CONFIG: Record<EligibilityStatus, { label: string; className: string; iconName: string }> = {
  eligible: { label: 'Eligible', className: 'bg-green-100 text-green-700', iconName: 'streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form' },
  partial: { label: 'Partial', className: 'bg-amber-100 text-amber-700', iconName: 'streamline:warning-triangle' },
  not_eligible: { label: 'Not Eligible', className: 'bg-red-100 text-red-700', iconName: 'streamline:interface-delete-circle-button-delete-remove-add-circle-buttons' },
  no_requirements: { label: 'Open Access', className: 'bg-blue-100 text-blue-700', iconName: 'streamline:shield-check' },
};

const ELIGIBILITY_FILTERS: { value: EligibilityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'eligible', label: 'Eligible' },
  { value: 'no_requirements', label: 'Open Access' },
  { value: 'partial', label: 'Partial' },
  { value: 'not_eligible', label: 'Not Eligible' },
];

// ─── Page ───────────────────────────────────────────────────────────────────────

export function FacilityAccessPage() {
  const navigate = useNavigate();
  const { isConnected } = useWalletStore();

  const [filter, setFilter] = useState<EligibilityFilter>('all');
  const [search, setSearch] = useState('');
  const { page, setPage, pageSize, setPageSize, paginate } = usePagination(TABLE_PAGE_SIZE_OPTIONS[0]);
  const [onchainIds, setOnchainIds] = useState<OnchainId[]>(MOCK_ONCHAIN_IDS);
  const [isCreatingId, setIsCreatingId] = useState(false);

  const activeIds = useMemo(() => (isConnected ? onchainIds : []), [isConnected, onchainIds]);

  function handleCreateOnchainId() {
    setIsCreatingId(true);
    setTimeout(() => {
      const newId: OnchainId = {
        id: `oid-new-${Date.now()}`,
        address: '0x1234567890abcdef1234567890abcdef12345678',
        claims: [],
        source: 'ascend',
      };
      setOnchainIds((prev) => [...prev, newId]);
      setIsCreatingId(false);
      toast.success('OnchainID created on Ascend Identity Registry');
    }, 2500);
  }

  const facilityEligibilities = useMemo(
    () => FACILITIES.map((f) => checkEligibility(f, activeIds)),
    [activeIds],
  );

  const handleFilterChange = (v: EligibilityFilter) => { setFilter(v); setPage(1); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  const filtered = useMemo(() => {
    let result = facilityEligibilities;

    if (filter !== 'all') {
      result = result.filter((f) => f.eligibility === filter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((f) =>
        f.facility.name.toLowerCase().includes(q) ||
        f.facility.collateralTokens.join(' ').toLowerCase().includes(q) ||
        f.facility.settlementTokens.join(' ').toLowerCase().includes(q),
      );
    }

    return result;
  }, [filter, search, facilityEligibilities]);

  const counts: Record<EligibilityFilter, number> = useMemo(() => ({
    all: facilityEligibilities.length,
    eligible: facilityEligibilities.filter((f) => f.eligibility === 'eligible').length,
    partial: facilityEligibilities.filter((f) => f.eligibility === 'partial').length,
    not_eligible: facilityEligibilities.filter((f) => f.eligibility === 'not_eligible').length,
    no_requirements: facilityEligibilities.filter((f) => f.eligibility === 'no_requirements').length,
  }), [facilityEligibilities]);

  const { paginated, totalItems } = paginate(filtered);

  const totalClaims = isConnected ? activeIds.reduce((s, oid) => s + oid.claims.length, 0) : 0;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Facility Access & Compliance</h1>
          <p className="text-sm text-muted-foreground">
            Your eligibility for each credit facility based on your onchain claims and compliance requirements.
          </p>
        </div>
      </div>

      {/* Demo control */}
      {isConnected && (
        <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-xs text-muted-foreground font-medium">Demo: Simulate OnchainID detection</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOnchainIds(MOCK_ONCHAIN_IDS)}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${activeIds.length > 0 ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                Has IDs ({MOCK_ONCHAIN_IDS.length})
              </button>
              <button
                onClick={() => setOnchainIds([])}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${activeIds.length === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                No IDs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total Facilities"
          value={String(FACILITIES.length)}
          subtitle="Available on platform"
          icon={<Icon name="streamline:bank" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Eligible"
          value={String(counts.eligible + counts.no_requirements)}
          subtitle="Ready to create margin accounts"
          icon={<Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-green-500" />}
        />
        <StatCard
          title="Ascend OnchainID"
          value={isConnected ? (activeIds.length > 0 ? 'Active' : 'Not created') : '-'}
          subtitle={isConnected ? (activeIds.length === 0 ? 'Create one to start' : `${totalClaims} verified claims`) : 'Connect wallet to check'}
          icon={<Icon name="streamline:fingerprint-2" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Not Eligible"
          value={String(counts.not_eligible + counts.partial)}
          subtitle="Missing required claims"
          icon={<Icon name="streamline:interface-delete-circle-button-delete-remove-add-circle-buttons" className="h-5 w-5 text-red-400" />}
        />
      </div>

      {/* Your Identity — single Ascend-scoped OnchainID per spec §3 */}
      {isConnected && activeIds.length > 0 && (
        <Card>
          <CardHeader className="p-6">
            <CardTitle className="flex items-center gap-2 text-base">
              <Icon name="streamline:fingerprint-2" className="h-4 w-4" />
              Your Identity
            </CardTitle>
            <CardDescription>
              You hold an Ascend-scoped OnchainID. Each credit facility checks the claims on
              this identity against its required claim topics.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border p-4">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground">OnchainID address</span>
                <span className="text-sm font-mono font-medium">
                  {activeIds[0].address.slice(0, 8)}…{activeIds[0].address.slice(-6)}
                </span>
              </div>
              <div className="rounded-lg border border-border p-4">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground">Bound registry</span>
                <span className="text-sm font-medium">Ascend Default Registry</span>
              </div>
              <div className="rounded-lg border border-border p-4">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground">Claims attached</span>
                <span className="text-sm font-medium">{totalClaims} verified</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No OnchainID: Create prompt */}
      {isConnected && activeIds.length === 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Icon name="streamline:warning-triangle" className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-900">No OnchainID Detected</p>
                <p className="text-xs text-yellow-700 mt-1">
                  No identity was found for your wallet address. An OnchainID is required to verify
                  your eligibility for credit facilities with compliance requirements.
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-yellow-300 bg-white/60 p-4 space-y-3">
              <div className="flex items-start gap-2.5">
                <Icon name="streamline:fingerprint-2" className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Create an OnchainID</p>
                  <p className="text-xs text-yellow-700 mt-0.5">
                    Mint a new OnchainID on the Ascend Identity Registry. Once created, you can attach
                    claims to meet facility compliance requirements.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                disabled={isCreatingId}
                onClick={handleCreateOnchainId}
              >
                {isCreatingId ? (
                  <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" /> Creating OnchainID...</>
                ) : (
                  <><Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="h-4 w-4 mr-1.5" /> Create OnchainID on Ascend Registry</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Facility list */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle>Credit Facility Eligibility</CardTitle>
          <CardDescription>
            {isConnected
              ? 'Your eligibility status for each facility based on the claims on your Ascend OnchainID.'
              : 'Connect your wallet to check your eligibility for each credit facility.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <FilterTabs
              filters={ELIGIBILITY_FILTERS}
              active={filter}
              onChange={handleFilterChange}
              counts={counts}
            />
            <SearchBar
              value={search}
              onChange={handleSearch}
              placeholder="Search facilities..."
              className="w-full sm:w-64"
            />
          </div>

          {paginated.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
              <p className="text-sm text-muted-foreground">No facilities found.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {paginated.map((entry) => {
                const config = ELIGIBILITY_CONFIG[entry.eligibility];

                return (
                  <div
                    key={entry.facility.id}
                    className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between first:pt-0 last:pb-0"
                  >
                    <div className="flex items-start gap-3 sm:items-center min-w-0 flex-1">
                      <Icon
                        name={config.iconName}
                        className={`mt-0.5 h-5 w-5 flex-shrink-0 sm:mt-0 ${
                          entry.eligibility === 'eligible' || entry.eligibility === 'no_requirements'
                            ? 'text-green-500'
                            : entry.eligibility === 'partial'
                              ? 'text-amber-500'
                              : 'text-red-400'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{entry.facility.name}</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>
                            {config.label}
                          </span>
                          {entry.facility.status === 'paused' && (
                            <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">Paused</span>
                          )}
                          {entry.facility.region && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{entry.facility.region}</span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span>Settlement: {entry.facility.settlementTokens.join(', ')}</span>
                          <span>Collateral: {entry.facility.collateralTokens.join(', ')}</span>
                          <span>Rate: {entry.facility.interestRate}</span>
                          <span>LTV: {entry.facility.maxLTV}</span>
                        </div>

                        {/* Requirements */}
                        {entry.facility.requirements.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {entry.facility.requirements.map((req) => {
                              const isMet = entry.metClaims.includes(req.claimType);
                              return (
                                <span
                                  key={req.claimType}
                                  className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
                                    isMet ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                                  }`}
                                >
                                  {isMet ? <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-2.5 w-2.5" /> : <Icon name="streamline:interface-delete-circle-button-delete-remove-add-circle-buttons" className="h-2.5 w-2.5" />}
                                  {req.claimType.replace(/_/g, ' ')}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {entry.facility.requirements.length === 0 && (
                          <p className="mt-1.5 text-xs text-green-600">No compliance requirements: open to all verified wallets</p>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 pl-8 sm:pl-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/credit-facilities/${entry.facility.id}`)}
                      >
                        View Facility <Icon name="streamline:interface-arrows-button-right-arrow-right-keyboard" className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <PaginationControls
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={totalItems}
            label="facilities"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, icon }: { title: string; value: string; subtitle?: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}
